<template>
  <table style="width: 100%">
    <thead>
      <tr>
        <th
          scope="col"
          class="text-center text-info"
          role="button"
          @click="setSortBy(sortBy)"
        >
          <i :class="`fa fa-sort-amount-${sortByDir ? 'asc' : 'desc'}`"></i>
        </th>
        <th
          scope="col"
          role="button"
          @click="setSortBy('holdingsLeft')"
          :class="{ 'text-info': sortBy === 'holdingsLeft' }"
        >
          {{ $t("profitloss.quantity") }}
        </th>
        <th
          scope="col"
          role="button"
          @click="setSortBy('assetPrice')"
          :class="{ 'text-info': sortBy === 'assetPrice' }"
        >
          {{ $t("profitloss.buy_price") }}
        </th>
        <th
          scope="col"
          role="button"
          @click="setSortBy('buy_value')"
          :class="{ 'text-info': sortBy === 'buy_value' }"
        >
          {{ $t("profitloss.buy_value") }}
        </th>
        <th
          scope="col"
          role="button"
          @click="setSortBy('valuation')"
          :class="{ 'text-info': sortBy === 'valuation' }"
        >
          {{ $t("global.valuation") }}
        </th>
        <th scope="col" class="text-center px-0"></th>
        <th
          scope="col"
          role="button"
          @click="setSortBy('pl')"
          :class="{ 'text-info': sortBy === 'pl' }"
        >
          {{ $t("profitloss.profitloss") }}
        </th>
        <th
          scope="col"
          role="button"
          @click="setSortBy('date')"
          :class="{ 'text-info': sortBy === 'date' }"
        >
          {{ $t("profitloss.buy_date") }}
        </th>
        <th scope="col" class="text-center px-0">
          {{ $t("global.notes") }}
        </th>
        <th scope="col" class="text-right pr-3">
          {{ $t("global.actions") }}
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-for="transaction in transactions">
        <tr v-if="transaction.holdingsLeft > 0" :key="transaction.id">
          <td></td>
          <td>
            {{ formatNumber(transaction.holdingsLeft) || "-" }}
          </td>
          <td>
            {{ formatMoney(transaction.assetPrice_currency) || "-" }}
          </td>
          <td>
            {{ formatMoney(transaction.buy_value) || "-" }}
          </td>
          <td>
            {{ formatMoney(transaction.valuation) || "-" }}
          </td>
          <td
            class="text-center px-0"
            :title="$t('profitloss.showInValuation_help')"
          >
            <b-form-checkbox
              @change="
                (checked) => toggleShowInValuation(transaction.id, checked)
              "
              :checked="transaction.showInValuation"
            />
          </td>
          <td
            :class="[
              'PLPTable__pl_colored',
              transaction.pl < 0 ? 'PLPTable__pl_colored--Negative' : '',
            ]"
          >
            {{ formatMoney(transaction.pl) || "-" }}
          </td>
          <td class="text-nowrap">
            {{ transaction.date | toLocaleDateString }}
          </td>
          <td class="text-center px-0">
            <AssetTableNotes
              v-if="!isMobile"
              :asset="transaction"
              :isLandscape="isLandscape"
              :updateAsset="updateTransaction"
              :label="$t('global.notes') + ':'"
              :placeholder="$t('profitloss.transaction_notes_placeholder')"
            />
            <router-link
              v-else
              :to="`/profitloss/transactions/${transaction.id}/edit`"
              :title="`${$t('global.edit')} ${$t('global.notes')}`"
              class="px-1 opacity-75"
              :class="[
                transaction.notes && transaction.notes.length > 0
                  ? 'text-warning'
                  : 'text-info',
              ]"
            >
              <i class="fa fa-lg fa-sticky-note"></i>
            </router-link>
          </td>
          <td class="text-nowrap text-right pr-3">
            <router-link
              :to="`/profitloss/transactions/${transaction.id}/sell`"
              class="btn btn-link p-0 opacity-75"
              :title="$t('profitloss.sell_close')"
            >
              <i class="fa fa-lg fa-arrow-down"></i>
            </router-link>
            <router-link
              :to="`/profitloss/transactions/${transaction.id}/edit`"
              class="btn btn-link p-0 ml-3 opacity-75"
              :title="$t('global.edit')"
            >
              <i class="fa fa-lg fa-pencil"></i>
            </router-link>
            <a
              href="/"
              class="btn btn-link p-0 ml-3 opacity-75"
              :title="$t('edit_asset_page.delete')"
              @click.prevent="removeTransactionClick(transaction.id)"
            >
              <i class="fa fa-lg fa-trash"></i>
            </a>
          </td>
        </tr>
      </template>
      <tr>
        <td colspan="10" class="text-right pr-4 py-2">
          {{ $t("profitloss.total_valuation") }}:
          <span class="font-weight-normal">{{
            formatMoney(asset.valuation) || "-"
          }}</span>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import { mapActions } from "vuex";
import NumbersMixin from "@/mixins/numbers";
import { sortByTextCol, sortByNumCol } from "@/services/arrays";
import AssetTableNotes from "./AssetTableNotes.vue";

const sortTransactions = (sortBy) => {
  return sortBy === "date" ? sortByTextCol(sortBy) : sortByNumCol(sortBy);
};

export default {
  mixins: [NumbersMixin],
  components: {
    AssetTableNotes,
  },
  props: {
    isMobile: Boolean,
    isLandscape: Boolean,
    asset: { type: Object, required: true },
    removeTransactionClick: { type: Function, required: true },
  },
  data() {
    return {
      sortBy: "date",
      sortByDir: false,
    };
  },
  computed: {
    transactions() {
      const transaction = [...this.asset.transactions];
      transaction.sort(sortTransactions(this.sortBy));

      if (!this.sortByDir) {
        return transaction.reverse();
      }
      return transaction;
    },
  },
  methods: {
    toggleShowTransaction(currencyId) {
      this.showTransactions = {
        ...this.showTransactions,
        [currencyId]: !this.showTransactions[currencyId],
      };
    },
    toggleShowInValuation(transactionId, checked) {
      const updateData = { id: transactionId, showInValuation: checked };
      this.updateTransaction(updateData);
    },
    setSortBy(column) {
      if (this.sortBy === column) {
        this.sortByDir = !this.sortByDir;
      } else {
        this.sortBy = column;
        this.sortByDir = true;
      }
    },
    ...mapActions({
      updateTransaction: "profitloss/updateTransaction",
    }),
  },
};
</script>